/**
 * ตรรกะล้วนของหน้าจอ Delivery แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * สามข้อที่แพงที่สุดของกลุ่มนี้อยู่ที่นี่ (invariant ของ Work Order นี้):
 *
 * 1. **THE PERMISSION HEADLINE** (`deliveryActionPermission`) — dispatch (สร้าง
 *    delivery) ต้องการ `SALES` + **CREATE** ส่วน reverse (ถอนการขาย) ต้องการ
 *    `SALES` + **APPROVE** คนละสิทธิ์กันโดยตั้งใจ (D-105 ข้อ 6): "คนที่ส่งของออกไป
 *    ได้ ไม่ได้แปลว่าถอนการขายได้" ฟังก์ชันนี้เป็นจุดเดียวที่ตัดสินว่าแต่ละปุ่มต้องการ
 *    สิทธิ์อะไร — ถ้าใครสลับให้ reverse ใช้ CREATE เทสต์ที่ผูกกับฟังก์ชันนี้ตรง ๆ
 *    ต้องแดง (mutation-checkable)
 * 2. **THE THREE STATES ARE NOT THE SAME ACT** (`deliveryLifecycleActionFor`) —
 *    `load` เสนอเฉพาะกล่องที่ `PACKED` และ `dispatch` เสนอเฉพาะกล่องที่ `LOADED`
 *    เท่านั้น อ่านตรงจาก `LOAD_SOURCE_PACKING_STATUSES`/
 *    `DISPATCH_SOURCE_PACKING_STATUSES` ของ `service.py`
 * 3. **การกรอกเหตุผลก่อนถอนการขาย** (`isValidReversalReason`) — เซิร์ฟเวอร์บังคับ
 *    ด้วย `ck_delivery_reversal_record_is_whole` (ต้องไม่ว่าง) ปุ่มถอนการขายต้อง
 *    ปิดจนกว่าจะกรอก ไม่ใช่ส่งสตริงว่างไปให้เซิร์ฟเวอร์ปฏิเสธ
 *
 * ⚠️ ไฟล์นี้ไม่มีฟังก์ชันที่ประกอบ `delivery_no`, `packing_unit_id`, สถานะ หรือ
 * ฟิลด์ผู้กระทำเลย — ทั้งหมดเป็นของเซิร์ฟเวอร์
 */

import {
  DISPATCHED_PACKING_STATUS,
  DRAFT_PACKING_STATUS,
  LOADED_PACKING_STATUS,
  PACKED_PACKING_STATUS,
  PACKING_PACKING_STATUS,
  RETURNED_PACKING_STATUS,
  REVERSAL_REASON_MAX_LENGTH,
  type PackingUnitSummary,
} from './types';

/* ------------------------------------------------------------- สถานะ ----- */

export interface PackingUnitStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/** หกสถานะของ lifecycle ตามลำดับที่ `packing.py` ประกาศไว้ — คำอธิบายเขียนจาก
 * มุมของหน้าจอ Delivery (โหลด/ส่ง/ถอน) ต่างจากคำอธิบายของหน้าจอ Packing */
export const PACKING_UNIT_STATUS_DESCRIPTORS: readonly PackingUnitStatusDescriptor[] = [
  {
    code: DRAFT_PACKING_STATUS,
    label: 'ร่าง (DRAFT)',
    summary: 'ยังไม่ได้แพ็ก — ไม่มีปุ่มใดบนหน้าจอนี้',
  },
  {
    code: PACKING_PACKING_STATUS,
    label: 'กำลังแพ็ก (PACKING)',
    summary: 'ยังไม่ปิดกล่อง — ต้องยืนยันที่หน้าจอ Packing ก่อนจึงจะโหลดขึ้นรถได้',
  },
  {
    code: PACKED_PACKING_STATUS,
    label: 'ปิดกล่องแล้ว (PACKED)',
    summary: 'พร้อมโหลดขึ้นรถ — ยังเป็นสต๊อกของโรงงานนี้อยู่ ไม่มีอะไรเคลื่อน',
  },
  {
    code: LOADED_PACKING_STATUS,
    label: 'ขึ้นรถแล้ว (LOADED)',
    summary: 'อยู่บนรถแล้วแต่ยังไม่ออกประตู — ยังไม่ขยับสต๊อก จนกว่าจะสแกนออก',
  },
  {
    code: DISPATCHED_PACKING_STATUS,
    label: 'ส่งออกแล้ว (DISPATCHED)',
    summary: 'ออกจากประตูแล้ว — สต๊อกออกจากบริษัทจริง ดูรายการเดลิเวอรีด้านล่าง',
  },
  {
    code: RETURNED_PACKING_STATUS,
    label: 'ถูกตีกลับ (RETURNED)',
    summary: 'การขายถูกถอนแล้ว — ของกลับเข้าโรงงาน ไม่มีปุ่มใดบนหน้าจอนี้',
  },
];

export type PackingUnitStatusTone = 'neutral' | 'warning' | 'success' | 'info' | 'danger';

export function packingUnitStatusTone(status: string): PackingUnitStatusTone {
  switch (status) {
    case DRAFT_PACKING_STATUS:
    case PACKING_PACKING_STATUS:
      return 'warning';
    case PACKED_PACKING_STATUS:
      return 'info';
    case LOADED_PACKING_STATUS:
      return 'info';
    case DISPATCHED_PACKING_STATUS:
      return 'success';
    case RETURNED_PACKING_STATUS:
      return 'danger';
    default:
      return 'neutral';
  }
}

export function packingUnitStatusLabel(status: string): string {
  return (
    PACKING_UNIT_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status
  );
}

/* ---------------------------------------------------------- lifecycle ---- */

/** สอง action ที่หน้าจอนี้ขับได้บนกล่อง — `confirm`/`unpack` เป็นของ Packing
 * (แยก Work Order) ไม่ใช่ของที่นี่ */
export const DELIVERY_LIFECYCLE_ACTIONS = ['load', 'dispatch'] as const;
export type DeliveryLifecycleAction = (typeof DELIVERY_LIFECYCLE_ACTIONS)[number];

/**
 * ใบในสถานะนี้ทำอะไรต่อได้บ้างบนหน้าจอนี้ — อ่านตรงจาก
 * `LOAD_SOURCE_PACKING_STATUSES`/`DISPATCH_SOURCE_PACKING_STATUSES` ของ
 * `service.py`: **THE THREE STATES ARE NOT THE SAME ACT** — `load` รับเฉพาะ
 * `PACKED` และ `dispatch` (การสร้าง delivery) รับเฉพาะ `LOADED` เท่านั้น สี่สถานะ
 * ที่เหลือไม่มีปุ่มบนหน้าจอนี้เลย
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย
 */
export function deliveryLifecycleActionFor(status: string): DeliveryLifecycleAction | null {
  switch (status) {
    case PACKED_PACKING_STATUS:
      return 'load';
    case LOADED_PACKING_STATUS:
      return 'dispatch';
    default:
      return null;
  }
}

export type DeliveryPermissionAction = 'EDIT' | 'CREATE' | 'APPROVE';

/**
 * **THE PERMISSION HEADLINE** — สิทธิ์ที่แต่ละปุ่มต้องการ ตรงกับด่านจริงฝั่ง
 * เซิร์ฟเวอร์ทีละเส้น (`require_permission` ใน `router.py`):
 * `load` = `EDIT`, `dispatch` (`POST /v1/deliveries`) = **`CREATE`**,
 * `reverse` = **`APPROVE`**
 *
 * D-105 ข้อ 6: คนที่ส่งของออกไปได้ (`CREATE`) ไม่ได้แปลว่าถอนการขายได้ — สอง
 * สิทธิ์นี้ต้อง**ต่างกัน**เสมอ ไม่ใช่แค่ต่างชื่อ action
 */
export function deliveryActionPermission(
  action: DeliveryLifecycleAction | 'reverse',
): DeliveryPermissionAction {
  switch (action) {
    case 'load':
      return 'EDIT';
    case 'dispatch':
      return 'CREATE';
    case 'reverse':
      return 'APPROVE';
  }
}

export function deliveryActionLabel(action: DeliveryLifecycleAction): string {
  switch (action) {
    case 'load':
      return 'โหลดขึ้นรถ';
    case 'dispatch':
      return 'ส่งออกจากประตู';
  }
}

/** `unpack`/`confirm` ของ Packing ไม่ใช่ของหน้าจอนี้ — helper ตรวจว่าแถวนี้เสนอ
 * `dispatch` ไหม ใช้ตรงในเทสต์ (acceptance ข้อสาม: "dispatch ต้องการ LOADED เท่านั้น") */
export function offersLoad(status: string): boolean {
  return deliveryLifecycleActionFor(status) === 'load';
}

export function offersDispatch(status: string): boolean {
  return deliveryLifecycleActionFor(status) === 'dispatch';
}

/* -------------------------------------------------------------- ฟอร์ม ----- */

/**
 * เหตุผลของการถอนการขายใช้ได้หรือยัง — ต้องไม่ว่างเปล่าและไม่เกินความยาวที่
 * `delivery.py REVERSAL_REASON_LENGTH` กำหนด
 *
 * **A REVERSAL ASKS FOR A REASON AND THE SERVER REQUIRES ONE** — ปุ่มถอนการขาย
 * ต้องปิดจนกว่าฟังก์ชันนี้จะคืน `true` ไม่ใช่ส่งสตริงว่างไปให้เซิร์ฟเวอร์ปฏิเสธ
 */
export function isValidReversalReason(reason: string): boolean {
  const trimmed = reason.trim();
  return trimmed !== '' && trimmed.length <= REVERSAL_REASON_MAX_LENGTH;
}

/** ตัวกรองเลขใบสั่งขาย — คืน `undefined` เมื่อว่าง (ไม่ส่ง `?so_number=`) เป็น
 * ตัวกรองจริงของเซิร์ฟเวอร์ ไม่ใช่ตัวกรองฝั่งเบราว์เซอร์ */
export function toSoNumberFilter(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** ตัวกรองรหัสลูกค้าของรายการเดลิเวอรี — คืน `undefined` เมื่อว่าง */
export function toCustomerCodeFilter(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** แถวที่หน้าจอนี้แสดงในตารางกล่อง — เฉพาะกล่องที่ยังอยู่ใน flow โหลด/ส่งของ
 * (`PACKED`/`LOADED`) หรือกำลังจะมีคนถามว่าทำไมกล่องนี้ไม่มีปุ่ม (สี่สถานะที่เหลือ
 * ยังคงแสดงในตารางเพื่อให้เห็นภาพรวม เพียงไม่มีปุ่มให้กด) — helper นี้ไม่กรองแถวออก
 * เพียงบอกว่ามีปุ่มไหม ใช้คู่กับ `deliveryLifecycleActionFor` */
export function hasDeliveryAction(item: PackingUnitSummary): boolean {
  return deliveryLifecycleActionFor(item.status) !== null;
}
